import type { Exercise } from "./schemas/exercise.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { TargetItem } from "./schemas/program.js";
import type { TimedSessionResult } from "./progressTypes.js";
import {
  catalogBySlug,
  hardSetCreditFor,
  normalizeVolumeByMuscle,
  resolveExerciseMuscleCredits,
  type AggregatorExerciseRow,
} from "./volumeByMuscle.js";
import { rollingWindowStart } from "./weekWindow.js";

// AP target actuals (2026-07-07). The numbers that ground a weekly target: what
// the athlete is ACTUALLY doing, per (axis, key, metric), so the editor shows a
// reference ("you average 12/week, last week 15") instead of asking for a goal
// in a vacuum. Also the source of the meter's muscle hard-set actual. All pure,
// reading the persisted SessionResult (exerciseResults + volumeByMuscle), never
// re-rolling per-set laterality math.
//
// Supported here: muscle×hard-sets, muscle×tonnage, exercise×hard-sets,
// exercise×tonnage. `reps` is NOT supported (SessionResult persists no total
// rep count per exercise, only the top set) — the editor doesn't offer it.
// `pattern` is legacy (not offered by the editor).

type ExerciseResultRow = TimedSessionResult["result"]["exerciseResults"][number];

/** One session's sets per muscle, added into `out`: `countOf` of each exercise
 *  result times its hard-set credit. Catalog misses credit nothing. */
export const creditedSetsByMuscle = (
  exerciseResults: ReadonlyArray<ExerciseResultRow>,
  bySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  countOf: (er: ExerciseResultRow) => number,
  out: Record<string, number> = {},
): Record<string, number> => {
  for (const er of exerciseResults) {
    const n = countOf(er);
    if (n <= 0) continue;
    const ex = bySlug.get(er.exerciseSlug);
    if (!ex) continue;
    for (const [m, credit] of resolveExerciseMuscleCredits(ex)) {
      out[m] = (out[m] ?? 0) + n * credit;
    }
  }
  return out;
};

type TargetKey = Pick<TargetItem, "axis" | "key" | "metric">;

// Hard sets per muscle over [fromMs, toMs], each persisted hardSetCount times
// its credit (resolveExerciseMuscleCredits, #538). Totals can be fractional;
// display rounds to ≤1dp. The caller owns the window.
export const hardSetsByMuscle = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  fromMs: number,
  toMs: number,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): Record<string, number> => {
  const bySlug = index ?? catalogBySlug(catalog);
  const out: Record<string, number> = {};
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    creditedSetsByMuscle(s.result.exerciseResults, bySlug, (er) => er.hardSetCount ?? 0, out);
  }
  return out;
};

// Slugs of exercises with hard sets in [fromMs, toMs] that the catalog lacks
// (distinct, sorted). hardSetsByMuscle credits them to no muscle, so a non-empty
// list means the muscle hard-set numbers may undercount. Same window and
// `hardSetCount > 0` filter as hardSetsByMuscle; counts are not changed.
export const uncataloguedExerciseSlugs = (
  sessions: TimedSessionResult[],
  bySlug: ReadonlyMap<string, Exercise>,
  fromMs: number,
  toMs: number,
): string[] => {
  const missing = new Set<string>();
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    for (const er of s.result.exerciseResults) {
      if ((er.hardSetCount ?? 0) <= 0) continue;
      if (!bySlug.has(er.exerciseSlug)) missing.add(er.exerciseSlug);
    }
  }
  return [...missing].sort();
};

// #116 case 2: the exercises a session of [fromMs, toMs] lists in
// `bodyweightMissing`: computed with no bodyweight on the profile, their
// bodyweight sets counted zero volume and no hard set until the admin
// recompute runs. The compute decided them from the sets (`kind`), so nothing
// here guesses from catalog flags. Rows older than the field are not seen.
// Distinct, sorted.
export const bodyweightMissingSlugs = (
  sessions: TimedSessionResult[],
  fromMs: number,
  toMs: number,
): string[] => {
  const found = new Set<string>();
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    for (const slug of s.result.bodyweightMissing ?? []) found.add(slug);
  }
  return [...found].sort();
};

// One session's contribution to a (axis, key, metric) — the shared routing so
// the buckets and the meter agree. Exported so the program-target PROJECTION
// (AP-10) can score a synthesised prescription session by the identical rule
// the logged actuals use, at single-session (per-day) granularity.
export const sessionTargetMetric = (
  s: TimedSessionResult,
  bySlug: ReadonlyMap<string, Exercise>,
  item: TargetKey,
): number => {
  if (item.axis === "muscle") {
    if (item.metric === "tonnage") {
      return normalizeVolumeByMuscle(s.result.volumeByMuscle)[
        item.key as CatalogMuscleSlug
      ] ?? 0;
    }
    if (item.metric === "hard-sets") {
      let n = 0;
      for (const er of s.result.exerciseResults) {
        const hs = er.hardSetCount ?? 0;
        if (hs <= 0) continue;
        const ex = bySlug.get(er.exerciseSlug);
        if (!ex) continue;
        n += hs * hardSetCreditFor(ex, item.key);
      }
      return n;
    }
    return 0;
  }
  if (item.axis === "exercise") {
    let n = 0;
    for (const er of s.result.exerciseResults) {
      if (er.exerciseSlug !== item.key) continue;
      if (item.metric === "tonnage") n += er.totalVolumeKg;
      else if (item.metric === "hard-sets") n += er.hardSetCount ?? 0;
    }
    return n;
  }
  return 0;
};

// The windowed actual over [fromMs, toMs] for a single target — the number the
// meter scores against `value`. Sums the SAME `sessionMetric` routing the editor
// reference uses, so meter and editor never disagree on what "actual" means for
// a (axis, key, metric). Muscle×hard-sets here equals hardSetsByMuscle for that
// key by construction (both route through resolveExerciseMuscleCredits).
export const weeklyActualFor = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  item: TargetKey,
  fromMs: number,
  toMs: number,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): number => {
  const bySlug = index ?? catalogBySlug(catalog);
  let n = 0;
  for (const s of sessions) {
    if (s.startedAtMs < fromMs || s.startedAtMs > toMs) continue;
    n += sessionTargetMetric(s, bySlug, item);
  }
  return n;
};

export type TargetActualStats = {
  // The LAST 7 DAYS up to now (rolling), the same window the meter scores (#463).
  thisWeek: number;
  // #464: the 4 rolling 7-day windows BEFORE the last 7 days, oldest → newest.
  // The last 7 days are NOT one of them: the volume gap (MEV minus the average)
  // compares the current window with an average that does not contain it.
  fullWeeks: number[];
  // fullWeeks total / 4: the average 7-day window. 0 when there is none.
  avgPerWeek: number;
  // Any nonzero in the 4 prior windows (else "no full week yet", never an
  // invented average).
  hasFullWeek: boolean;
  // Any nonzero in the prior windows OR the last 7 days.
  hasData: boolean;
};

/** The rolling 7-day window `k` back: k=0 is the last 7 days [start, now];
 *  k=1..4 are the windows before it, [start, end) so no session lands twice.
 *  ONE helper for the actual and the average (#464): they cannot drift. */
export const rollingWeekIndex = (startedAtMs: number, nowMs: number): number => {
  if (!(startedAtMs <= nowMs)) return -1;
  for (let k = 0; k <= 4; k++) {
    if (startedAtMs >= rollingWindowStart(nowMs, 7 * (k + 1))) return k;
  }
  return 5;
};

// The reference the editor + coverage show for a target: the average 7-day
// window + the last 7 days, for the selected (axis, key, metric). Windows are
// ROLLING, anchored to `nowMs` (#464), so `thisWeek` equals the meter's current
// actual (#463). The average is the 4 windows BEFORE the last 7 days, Antenore's
// call of 26/10 (it superseded PG-17's calendar weeks).
export const weeklyTargetActualStats = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  item: TargetKey,
  nowMs: number,
  /** PG-16 — the catalog index, when the caller already built it. These three
   *  are called in a LOOP (one per muscle row, one per target row), and each
   *  rebuilt the same Map from the same unchanged catalog every time. Optional
   *  so the single-shot callers keep working untouched. */
  index?: ReadonlyMap<string, Exercise>,
): TargetActualStats => {
  const bySlug = index ?? catalogBySlug(catalog);
  const fullWeeks = [0, 0, 0, 0];
  let thisWeek = 0;
  for (const s of sessions) {
    const k = rollingWeekIndex(s.startedAtMs, nowMs);
    if (k < 0 || k > 4) continue; // outside the last 7 days + the 4 before
    const v = sessionTargetMetric(s, bySlug, item);
    if (v <= 0) continue;
    if (k === 0) thisWeek += v;
    else fullWeeks[4 - k] = (fullWeeks[4 - k] ?? 0) + v;
  }
  const fullTotal = fullWeeks.reduce((a, b) => a + b, 0);
  return {
    thisWeek,
    fullWeeks,
    avgPerWeek: fullTotal / 4,
    hasFullWeek: fullTotal > 0,
    hasData: fullTotal > 0 || thisWeek > 0,
  };
};
